import { isDefined, isNil } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useCountdownTimer } from "@src/appV2/lib/utils/useCountdownTimer";
import {
  TimeclockComplianceClockAction,
  TimeclockComplianceResourceType,
  useCreateTimeclockComplianceProofUploadUrl,
} from "@src/appV2/Shifts/Shift/api/useCreateShiftTimeclockComplianceProofUploadUrl";
import { ClockActionPictureDialogEvent } from "@src/appV2/Shifts/Shift/TakeClockActionPicture/ClockActionPictureDialogEvent";
import { uploadTimeclockProofVideoToS3 } from "@src/appV2/Shifts/Shift/TakeClockActionPicture/utils";
import { useEffect, useRef, useState } from "react";

import { TimeclockUploadErrors } from "../dialogs/VideoUploadErrorBottomSheet";
import { type ComplianceProofVideo } from "../TimeclockVideoProofFlow";

interface UseTimeclockVideoUploadParams {
  clockAction: "clock-in" | "clock-out";
  shiftId: string;
  workerId: string;
  workplaceId: string;
  logData: Record<string, any>;
  onSuccess: (video: ComplianceProofVideo) => Promise<void>;
  logTimeclockEvent: (
    eventAction: ClockActionPictureDialogEvent,
    meta?: Record<string, any>
  ) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
  onError?: (error: string) => void;
  linearProgressDurationSeconds?: number;
}

export function useTimeclockVideoUpload(params: UseTimeclockVideoUploadParams) {
  const {
    clockAction,
    shiftId,
    logData,
    onSuccess,
    logTimeclockEvent,
    onUploadStart,
    onUploadEnd,
    onError,
    linearProgressDurationSeconds,
  } = params;

  const videoBlobRef = useRef<Blob>();
  const videoResourceRef = useRef<ComplianceProofVideo>();
  const abortControllerRef = useRef<AbortController>();

  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<number>();
  const [errorText, setErrorText] = useState<string>();

  const { secondsLeft, startTimer, resetTimer } = useCountdownTimer({
    durationInSeconds: linearProgressDurationSeconds ?? 10,
  });

  const handleError = (error: string) => {
    setErrorText(error);
    onError?.(error);
  };

  const { mutateAsync: getS3PresignedUploadUrl } = useCreateTimeclockComplianceProofUploadUrl();

  const reset = () => {
    videoBlobRef.current = undefined;
    videoResourceRef.current = undefined;
    setIsUploading(false);
    setProgress(undefined);
    setErrorText(undefined);
  };

  // Abort and reset when clockAction changes (e.g. component re-used for clock-out)
  useEffect(() => {
    abortControllerRef.current?.abort();
    reset();
  }, [clockAction]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      resetTimer();
    };
  }, [resetTimer]);

  useEffect(() => {
    if (isDefined(linearProgressDurationSeconds) && isUploading) {
      const currentProgress = 1 - secondsLeft / (linearProgressDurationSeconds ?? 10);
      setProgress(currentProgress * 0.9);
    }
  }, [secondsLeft, linearProgressDurationSeconds, isUploading]);

  async function onUploadSettled(isSuccess: boolean) {
    // Skip further handling if caller aborted.
    if (abortControllerRef.current?.signal.aborted) {
      setIsUploading(false);
      onUploadEnd?.();
      return;
    }

    if (!isSuccess) {
      setIsUploading(false);
      onUploadEnd?.();
      return;
    }

    try {
      if (isDefined(videoResourceRef.current)) {
        logTimeclockEvent(ClockActionPictureDialogEvent.UPLOAD_SUCCESS);
        await onSuccess(videoResourceRef.current);

        setProgress(1);
      } else {
        logTimeclockEvent(ClockActionPictureDialogEvent.UPLOAD_FAILURE, {
          source: "clock-action",
          error: "No video resource found",
        });
        handleError(TimeclockUploadErrors.UnknownError);
      }
    } catch (error) {
      logTimeclockEvent(ClockActionPictureDialogEvent.UPLOAD_FAILURE, {
        source: "clock-action",
        error: error instanceof Error ? error.message : "Unknown error",
      });
      handleError(TimeclockUploadErrors.UnknownError);
    }

    setIsUploading(false);
    onUploadEnd?.();
  }

  const startUpload = async (blob: Blob) => {
    if (isUploading) {
      logTimeclockEvent(ClockActionPictureDialogEvent.UPLOAD_FAILURE, {
        source: "duplicate-upload-attempt",
        error: "Upload already in progress",
      });
      return;
    }

    videoBlobRef.current = blob;

    if (!blob?.size) {
      logTimeclockEvent(ClockActionPictureDialogEvent.VIDEO_RECORDING_FAILED, {
        error: "Missing videoBlob at upload",
        hasVideoBlob: isDefined(blob),
        blobSize: blob?.size,
      });
      handleError(TimeclockUploadErrors.InvalidVideo);
      await onUploadSettled(false);
      return;
    }

    logTimeclockEvent(ClockActionPictureDialogEvent.UPLOAD_START, {
      blobSize: blob.size,
    });

    if (isDefined(linearProgressDurationSeconds)) {
      startTimer();
    }

    setIsUploading(true);
    onUploadStart?.();
    setProgress(0);
    abortControllerRef.current = new AbortController();

    try {
      const uploadUrlResponse = await getS3PresignedUploadUrl({
        shiftId,
        clockAction:
          clockAction === "clock-in"
            ? TimeclockComplianceClockAction.CLOCK_IN
            : TimeclockComplianceClockAction.CLOCK_OUT,
        resourceType: TimeclockComplianceResourceType.MP4,
      });

      if (!uploadUrlResponse.data) {
        handleError(TimeclockUploadErrors.PresignedUrlError);
        await onUploadSettled(false);
        return;
      }

      const { uploadUrl, bucketName, fileKey } = uploadUrlResponse.data.attributes;
      const { isSuccess } = await uploadTimeclockProofVideoToS3({
        uploadUrl,
        videoBlob: blob,
        progressCallback: (progress) => {
          if (isNil(linearProgressDurationSeconds)) {
            setProgress(progress);
          }
        },
        abortSignal: abortControllerRef.current.signal,
        logData: {
          ...logData,
          bucketName,
          fileKey,
          uploadUrlLength: uploadUrl.length,
          blobSize: blob.size,
        },
      });

      if (isSuccess) {
        videoResourceRef.current = { bucketName, fileKey };
      } else if (!abortControllerRef.current?.signal.aborted) {
        handleError(TimeclockUploadErrors.UploadError);
      }

      await onUploadSettled(isSuccess);
    } catch (error: unknown) {
      logTimeclockEvent(ClockActionPictureDialogEvent.UPLOAD_FAILURE, {
        source: "presigned-upload-url",
        blobSize: blob.size,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      logError(APP_V2_APP_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_ERROR, {
        error,
        metadata: { logData, location: "presigned-upload-url" },
      });

      handleError(TimeclockUploadErrors.PresignedUrlError);
      await onUploadSettled(false);
    }
  };

  const retryUpload = async () => {
    if (videoBlobRef.current && videoBlobRef.current.size > 0) {
      logTimeclockEvent(ClockActionPictureDialogEvent.REUPLOAD_VIDEO);
      await startUpload(videoBlobRef.current);
      return;
    }

    logTimeclockEvent(ClockActionPictureDialogEvent.RETAKE_VIDEO);
    reset();
  };

  const abortUpload = () => {
    abortControllerRef.current?.abort();
    setIsUploading(false);
    onUploadEnd?.();
    setProgress(undefined);
    logTimeclockEvent(ClockActionPictureDialogEvent.UPLOAD_CANCELLED);
  };

  return {
    isUploading,
    progress,
    errorText,
    videoBlob: videoBlobRef.current,
    startUpload,
    retryUpload,
    abortUpload,
    reset,
  } as const;
}
